import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Fragment } from "react";
import useSWRImmutable from "swr/immutable";
import { defaultGetFetcher, IErrorResponse } from "../../../api/base";

// Mirrors the willow-client AdvisorProfile shape (AdvisorProfileInformation.to_dto()).
interface AdvisorIntro {
  description?: string;
  video?: string;
  animal?: string;
  mbti?: string;
  hobby?: string;
  zodiac?: string;
  birthOrder?: string;
}

interface AdvisorFullProfile {
  id: number;
  firstName: string;
  lastName: string;
  photos?: string[] | null;
  shortBio?: string | null;
  bio?: string | null;
  intro?: AdvisorIntro | null;
  firmName?: string | null;
  firmBio?: string | null;
  firmLogo?: string | null;
  firmSec?: string | null;
  advisorSec?: string | null;
  disclosure?: string | null;
  designations?: string[] | null;
  expertise?: string[] | null;
  specialities?: unknown[] | null;
  languages?: string[] | null;
  statesCovered?: string[] | null;
  schedulingLink?: string | null;
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-y-2">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </div>
);

const Badges = ({ items }: { items?: (string | unknown)[] | null }) => {
  const values = (items ?? []).filter(Boolean).map((i) => String(i));
  if (values.length === 0) return <p className="text-xs text-gray-400">None listed</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((value, index) => (
        <span
          key={`${value}-${index}`}
          className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800 ring-1 ring-inset ring-primary-200"
        >
          {value}
        </span>
      ))}
    </div>
  );
};

const Html = ({ html }: { html?: string | null }) => {
  if (!html) return <p className="text-xs text-gray-400">Not provided</p>;
  return <div className="prose prose-sm max-w-none text-xs text-gray-700" dangerouslySetInnerHTML={{ __html: html }} />;
};

interface AdvisorProfileDialogProps {
  advisorId: number | null;
  onClose: () => void;
}

export const AdvisorProfileDialog = ({ advisorId, onClose }: AdvisorProfileDialogProps) => {
  const {
    data: profile,
    isLoading,
    error,
  } = useSWRImmutable<AdvisorFullProfile, IErrorResponse>(
    advisorId ? `api/v3/admin/advertising/advisor/${advisorId}/profile` : null,
    (url: string) => defaultGetFetcher(url),
  );

  return (
    <Transition appear show={advisorId !== null} as={Fragment}>
      <Dialog open={advisorId !== null} onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-full sm:max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                    <div className="px-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                          Advisor profile
                        </Dialog.Title>
                        <button
                          type="button"
                          onClick={onClose}
                          className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                        </button>
                      </div>
                    </div>

                    <div className="relative mt-4 flex flex-1 flex-col gap-y-6 px-4 sm:px-6">
                      {isLoading && <p className="text-sm text-gray-500">Loading profile…</p>}
                      {error && <p className="text-sm text-red-600">Could not load advisor profile.</p>}

                      {profile && (
                        <>
                          {profile.photos && profile.photos.length > 0 && (
                            <div className="flex gap-3 overflow-x-auto">
                              {profile.photos.slice(0, 3).map((photo, index) => (
                                <img
                                  key={index}
                                  src={photo}
                                  alt={`${profile.firstName} ${index + 1}`}
                                  className="h-40 w-40 flex-none rounded-lg object-cover"
                                />
                              ))}
                            </div>
                          )}

                          <div className="flex flex-col gap-y-1">
                            <h2 className="text-xl font-bold text-gray-900">
                              {profile.firstName} {profile.lastName}
                            </h2>
                            {profile.firmName && <p className="text-sm text-gray-500">{profile.firmName}</p>}
                          </div>

                          {profile.shortBio && (
                            <Section title="Summary">
                              <Html html={profile.shortBio} />
                            </Section>
                          )}

                          {profile.intro && (profile.intro.mbti || profile.intro.hobby || profile.intro.animal) && (
                            <Section title="My story">
                              <dl className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                                {profile.intro.mbti && (
                                  <div>
                                    <dt className="font-medium text-gray-900">MBTI</dt>
                                    <dd>{profile.intro.mbti}</dd>
                                  </div>
                                )}
                                {profile.intro.hobby && (
                                  <div>
                                    <dt className="font-medium text-gray-900">Hobby</dt>
                                    <dd>{profile.intro.hobby}</dd>
                                  </div>
                                )}
                                {profile.intro.animal && (
                                  <div>
                                    <dt className="font-medium text-gray-900">Favorite animal</dt>
                                    <dd>{profile.intro.animal}</dd>
                                  </div>
                                )}
                                {profile.intro.zodiac && (
                                  <div>
                                    <dt className="font-medium text-gray-900">Zodiac</dt>
                                    <dd>{profile.intro.zodiac}</dd>
                                  </div>
                                )}
                                {profile.intro.birthOrder && (
                                  <div>
                                    <dt className="font-medium text-gray-900">Birth order</dt>
                                    <dd>{profile.intro.birthOrder}</dd>
                                  </div>
                                )}
                              </dl>
                            </Section>
                          )}

                          <Section title="Designations">
                            <Badges items={profile.designations} />
                          </Section>

                          <Section title="Advisory specialties">
                            <Badges items={profile.expertise} />
                          </Section>

                          <Section title="Languages">
                            <Badges items={profile.languages} />
                          </Section>

                          <Section title="States covered">
                            <Badges items={profile.statesCovered} />
                          </Section>

                          <Section title="Bio">
                            <Html html={profile.bio} />
                          </Section>

                          {(profile.firmName || profile.firmBio) && (
                            <Section title="Financial firm">
                              {profile.firmLogo && (
                                <img
                                  src={profile.firmLogo}
                                  alt={profile.firmName ?? "Firm"}
                                  className="h-12 w-auto max-w-full self-start object-contain"
                                />
                              )}
                              {profile.firmName && <p className="text-sm font-medium text-gray-900">{profile.firmName}</p>}
                              {profile.firmSec && <p className="text-xs text-gray-500">SEC: {profile.firmSec}</p>}
                              <Html html={profile.firmBio} />
                            </Section>
                          )}

                          {profile.disclosure && (
                            <Section title="Important disclosures">
                              <Html html={profile.disclosure} />
                            </Section>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
